import React, { useEffect, useState } from 'react';
import '../FinancasWaitingBadge/FinancasWaitingBadge.css';

/**
 * What Finanças has said about this case.
 *
 * Finanças answers by email to the lawyer, and the mail says only that the
 * request is concluded and that the answer waits in the portal. It names the
 * lawyer, never the applicant, so an email reaches this page only through the
 * reference recorded above: user-backend matches the number in the subject to
 * `financas_reference_key`.
 *
 * The reading is the part that carries the answer rather than the announcement
 * of one, and it does not depend on the email: the reader opens the portal from
 * the reference on the case. So a reading can arrive for a request whose
 * notification was never forwarded, and it is shown on its own
 * (`email_missing`) rather than withheld for want of an email.
 */

interface Attachment {
  filename: string | null;
  content_type: string | null;
  gcs_path: string | null;
}

interface Reading {
  status: string | null;
  read_at: string | null;
  report: string | null;
  blocked_writes: number;
  reference?: string | null;
  /** The request's state in the portal's own words ("Concluído"). */
  state?: string | null;
  /** The last message asks us for something. */
  awaiting_lawyer?: boolean;
  asked_for?: string | null;
  /** The applicant as the thread names them, dropped when the page did not. */
  applicant?: string | null;
  /** Finanças messages this lawyer had not seen before this visit. */
  new_messages?: number;
  /** The newest Finanças message shown, sent back to mark it seen. */
  latest_message_key?: string | null;
}

export interface FinancasEmail {
  id: string;
  subject: string;
  from: string | null;
  received_at: string | null;
  reference: string | null;
  status: string | null;
  body: string;
  body_truncated: boolean;
  attachments: Attachment[];
  reading: Reading | null;
  /** This entry is a portal reading; no email was forwarded for the request. */
  email_missing?: boolean;
}

interface Props {
  caseId: string;
  user: { getIdToken: () => Promise<string> } | null;
  /** The reference recorded on the case, to explain an empty list honestly. */
  reference?: string | null;
}

const formatReceived = (value: string | null) => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
};

// Opening the case page is what reading the thread in the portal used to be:
// whatever Finanças wrote is now seen, by this lawyer. Best effort, because a
// failure only means the case stays "new" on the list one visit longer.
const markSeen = async (
  caseId: string,
  user: { getIdToken: () => Promise<string> },
  emails: FinancasEmail[]
) => {
  const seen = new Set<string>();
  for (const email of emails) {
    const reading = email.reading;
    const ref = reading?.reference || email.reference;
    if (!reading?.new_messages || !reading.latest_message_key || !ref || seen.has(ref)) continue;
    seen.add(ref);
    try {
      const token = await user.getIdToken();
      await fetch(`${process.env.REACT_APP_API_URL}/cases/${caseId}/financas-seen`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reference: ref, through: reading.latest_message_key }),
      });
    } catch (err) {
      console.error('Could not mark Finanças messages seen:', err);
    }
  }
};

const FinancasEmails: React.FC<Props> = ({ caseId, user, reference }) => {
  const [emails, setEmails] = useState<FinancasEmail[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Guarded against a slow answer for the case we just left: switching cases
    // while a request is in flight would otherwise let the older response land
    // last and show one case's Finanças emails under another.
    let cancelled = false;
    // Clear first: without this the previous case's emails stay on screen
    // until the new answer arrives, which reads as though they belong here.
    setEmails(null);
    setError('');

    const load = async () => {
      if (!user || !caseId) return;
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/cases/${caseId}/financas-emails`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (cancelled) return;
        // The route returns a list, but a shape surprise should not take the
        // case page down with it.
        const list: FinancasEmail[] = Array.isArray(data) ? data : [];
        setEmails(list);
        // The "new" labels stay up for this visit: they come from the answer
        // above, not from what is marked here.
        markSeen(caseId, user, list);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching Finanças emails:', err);
        setError('Could not load Finanças emails.');
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [caseId, user]);

  if (error) {
    return (
      <div className="documents-section financas-emails">
        <h2>Finanças emails</h2>
        <p className="financas-office-error" role="alert">{error}</p>
      </div>
    );
  }

  if (emails === null) {
    return null;
  }

  return (
    <div className="documents-section financas-emails">
      <div className="section-header">
        <h2>Finanças updates</h2>
        {emails.length > 0 && (
          <span className="financas-emails-count">{emails.length}</span>
        )}
      </div>

      {emails.length === 0 && (
        <p className="financas-emails-empty">
          {reference
            ? 'Nothing has come back from Finanças for this reference yet.'
            : 'Record the Finanças reference above and emails quoting that number will appear here.'}
        </p>
      )}

      {emails.map(email => (
        <article key={email.id} className="financas-email" data-testid="financas-email">
          <header className="financas-email-header">
            <span className="financas-email-subject">
              {email.subject || (email.email_missing ? 'e-balcão request' : '(no subject)')}
            </span>
            <span className="financas-email-meta">
              {/* A reading-only entry is dated by when the portal was read:
                  there is no email to date it by. */}
              {email.email_missing
                ? `Read from the portal ${formatReceived(email.received_at)}`
                : formatReceived(email.received_at)}
              {email.from ? ` · ${email.from}` : ''}
              {email.reference ? ` · ${email.reference}` : ''}
            </span>
          </header>

          {/* Said plainly rather than left to the empty body: the notification
              not arriving says nothing about the request, and the answer below
              is the portal's, read straight from the reference on this case. */}
          {email.email_missing ? (
            <p className="financas-email-meta financas-email-no-mail">
              No email was forwarded for this request. Read directly from the portal.
            </p>
          ) : (
            <pre className="financas-email-body">{email.body}</pre>
          )}
          {email.body_truncated && (
            <p className="financas-email-meta">Shortened. The full message is in the inbox.</p>
          )}

          {email.attachments?.length > 0 && (
            <ul className="financas-email-attachments">
              {email.attachments.map((attachment, index) => (
                <li key={`${attachment.filename}-${index}`}>
                  {attachment.filename || 'attachment'}
                  {attachment.content_type ? ` · ${attachment.content_type}` : ''}
                </li>
              ))}
            </ul>
          )}

          {email.reading ? (
            <div className="financas-email-reading">
              <h3>
                Answer from the portal
                {/* What she would have seen as unread in the portal before
                    the reader opened it. */}
                {(email.reading.new_messages ?? 0) > 0 && ' '}
                {(email.reading.new_messages ?? 0) > 0 && (
                  <span className="case-financas-new-badge" data-testid="financas-new">
                    {email.reading.new_messages === 1
                      ? 'New message'
                      : `${email.reading.new_messages} new messages`}
                  </span>
                )}
              </h3>
              <p className="financas-email-meta">
                Read {formatReceived(email.reading.read_at)}
                {email.reading.state ? ` · ${email.reading.state}` : ''}
                {/* Only a run that did not succeed is worth a word here: a
                    "success" on every reading is noise beside the state. */}
                {email.reading.status && email.reading.status !== 'success'
                  ? ` · ${email.reading.status}`
                  : ''}
                {email.reading.applicant ? ` · ${email.reading.applicant}` : ''}
              </p>
              {/* The one line that asks for action, above the thread rather
                  than inside it: everything else here is a record, this is a
                  task. */}
              {email.reading.awaiting_lawyer && (
                <p className="financas-email-awaiting" data-testid="financas-awaiting">
                  Finanças is waiting on us
                  {email.reading.asked_for ? `: ${email.reading.asked_for}` : '.'}
                </p>
              )}
              <pre className="financas-email-body">{email.reading.report || '(no answer recorded)'}</pre>
            </div>
          ) : (
            <p className="financas-email-meta financas-email-unread">
              The answer is in the Portal das Finanças. Nobody has fetched it for this
              request yet.
            </p>
          )}
        </article>
      ))}
    </div>
  );
};

export default FinancasEmails;
